import { SubmitHandler, useForm } from "react-hook-form";
import Button from "../Button";
import Modal from "../Modal";
import SelectController from "../SelectController";
import { zodResolver } from "@hookform/resolvers/zod";
import { requestNewCardSchema, RequestNewCardValidationSchema } from "@/common/schemas/requestNewCard.schema";
import { useState } from "react";
import { UseMutationResult } from "@tanstack/react-query";
import { FormTransformers } from "@/common/utils/formTransformers";
import { ErrorResponse } from "@/common/types/errorResponse";
import TextArea from "../TextArea";
import { ModalMessageSimple } from "../ModalMessageSimple";
import { AxiosResponse } from "axios";
import useDebounce from "@/common/hooks/useDebounce";
import { useCardRequestReasons } from "@/common/hooks/queries/useCardRequestReasons";
import { Mapper } from "@/common/services/mapper";
import { CardRequestReasonsTransformer } from "@/common/services/cardRequestReasonsTransformer";

interface RequestNewCardModalProps {
  mutationHook: UseMutationResult<AxiosResponse<any, any, {}>, ErrorResponse, RequestNewCardValidationSchema, unknown>;
  operationKlassToCheckPermission: string;
}

const RequestNewCardModal = ({ operationKlassToCheckPermission, mutationHook }: RequestNewCardModalProps) => {
  const [isOpen, setIsOpen] = useState(false);
  const [messages, setMessages] = useState<string[] | boolean>(false);

  const [searchCardRequestReasons, setSearchCardRequestReasons] = useDebounce('');

  const {
    mutateAsync: requestNewCard,
    isPending: requestNewCardIsPending,
    isError: requestNewCardIsError,
  } = mutationHook;


  const { data: cardRequestReasons, isPending: cardRequestReasonsIsPending } = useCardRequestReasons({
    filters: { search: searchCardRequestReasons },
    mapper: Mapper.mapWithTransformer({ transformer: CardRequestReasonsTransformer.optionsTransformer() }),
  });

  const {
    register,
    control,
    handleSubmit,
    reset,
    setError,
    formState: { errors },
  } = useForm<RequestNewCardValidationSchema>({
    resolver: zodResolver(requestNewCardSchema),
  });

  const onSubmit: SubmitHandler<RequestNewCardValidationSchema> = async (form) => {
    try {
      await requestNewCard(form);

      setIsOpen(false);
      setMessages(['Cartão solicitado com sucesso!']);
    } catch (e: any | ErrorResponse) {
      FormTransformers.errorsTransformer({ setError, setMessages })(e);
    }
  };

  return (
    <>
      <Modal
        isOpen={isOpen}
        setIsOpen={(value) => {
          reset();
          setIsOpen(value);
        }}
        Trigger={(
          <Button
            operationKlassToCheckPermission={operationKlassToCheckPermission}
            variant="yellow"
          >
            Emitir 2° via do cartão
          </Button>
        )}
        useCancellableFooter
        confirmButtonIsLoading={requestNewCardIsPending}
        onConfirm={handleSubmit(onSubmit)}
        title="Emitir segunda via do cartão"
        body={(
          <form className="flex flex-col gap-4">
            <SelectController
              control={control}
              name="request_reason"
              label="Motivo da 2ª via"
              markAsRequired
              onInputChange={setSearchCardRequestReasons}
              options={cardRequestReasons}
              isLoading={cardRequestReasonsIsPending}
              errorMessage={errors.request_reason?.message || undefined}
            />

            <TextArea
              label="Suas observações"
              markAsRequired
              {...register('request_observation')}
              errorMessage={errors.request_observation?.message || undefined}
            />
          </form>
        )}
      />

      {messages && (
        <ModalMessageSimple
          messages={messages}
          setMessages={setMessages}
          isError={requestNewCardIsError}
        />
      )}
    </>
  );
}

export default RequestNewCardModal;
